<script setup lang="ts">
import type LxcTable from "@lxc/app-device-common/src/components/LxcTable.vue";
import type { ModelRef } from "vue";
import { useAcl } from "vue-simple-acl";
import { useSideNavigation } from "~/core/composables/useSideNavigation";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import { Filters } from "~/core/models/filters";
import type { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useFetchUsers } from "~/modules/user/composables/useFetchUsers";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import type { User } from "~/modules/user/models/User.interface";
import { UserActions } from "~/modules/user/models/UserActions.enum";
import { UserColumns } from "~/modules/user/models/UserColumns.enum";
import { getProfileLabel } from "~/modules/user/utils/profileLabel";

const { t } = useI18n();
const { can } = useAcl();

const props = defineProps<{
  businessUnitCode?: number;
  organizationCode?: number;
  selectedUsers?: User[];
  columns?: UserColumns[];
  organizationType?: OrganizationType;
  pathContext: string;
  useExternalIdp?: boolean;
}>();

const showUserForm: ModelRef<boolean> = defineModel<boolean>("showUserForm", {
  required: true,
});

const currentSelectedUser: ModelRef<string | undefined> = defineModel<
  string | undefined
>("currentSelectedUser");

const {
  isLoading,
  error,
  results,
  fetchData,
  filters,
  setFilter,
  onSearch,
  search,
  onSortChange,
} = useFetchUsers({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
  useQueryParametersForPagination: true,
});
const { navigateToPath, onClose } = useSideNavigation();

const tableRef = ref<InstanceType<typeof LxcTable>>();

const getGroupsLabel = (groups: GroupsUserV2[]): string[] => {
  return groups?.map((group) => group.name);
};

const searchQuery = computed({
  get() {
    return (filters.get(Filters.USER_FIRST_OR_LAST_NAME) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.USER_FIRST_OR_LAST_NAME, searchQuery);
  },
});

async function reloadWithContext() {
  await fetchData(
    results.value?.pagination.page,
    results.value?.pagination.pageSize,
  );
}

function reloadData() {
  fetchData();
}

async function onSelectUser(user: User) {
  if (can(ACLRoles.CAN_WRITE_USER)) {
    await navigateToPath(`${props.pathContext}/${user.login}`);
  }
}

function onCloseCanvas(event: CloseEvent) {
  onClose(event, props.pathContext, reloadData);
}

onSearch(fetchData);
</script>

<template>
  <div class="mb-4 w-1/2">
    <search-bar
      v-model:search-query="searchQuery"
      :search-placeholder="t('filters.searchByLastNameOrFirstName')"
      @clear="search"
      @search="search"
    />
  </div>

  <lxc-table
    ref="tableRef"
    :is-loading="isLoading"
    :data="results?.results"
    :empty-text="t('user.empty')"
    :context="results?.pagination"
    :error="error?.toError()"
    :clickable="can(ACLRoles.CAN_WRITE_USER)"
    @change-page-and-page-size="fetchData"
    @sort-change="onSortChange"
    @row-click="onSelectUser"
  >
    <lxc-table-column v-if="selectedUsers" type="selection" />
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.LAST_NAME)"
      prop="lastName"
      :label="t('user.table.column.lastName')"
    />
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.FIRST_NAME)"
      prop="firstName"
      :label="t('user.table.column.firstName')"
    />
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.PROFILE)"
      :label="t('user.table.column.profile')"
      class="!py-0"
    >
      <template #default="scope">
        <badge-for-list
          :label="getProfileLabel(scope.row.profile)"
          type="secondary"
        />
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.EMAIL)"
      prop="email"
      :label="t('user.table.column.email')"
    />
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.GROUPS)"
      :label="t('user.table.column.groups')"
      class="!py-0"
    >
      <template #default="scope">
        <badge-for-list
          :label="getGroupsLabel(scope.row.groups)"
          type="secondary"
        />
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.STATUS)"
      :label="t('user.table.column.status')"
      width="8rem"
    >
      <template #default="scope">
        {{
          scope.row.active ? t("user.status.active") : t("user.status.inactive")
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="can(ACLRoles.CAN_WRITE_USER) && !useExternalIdp"
      class="!py-0 !px-0"
      width="6rem"
    >
      <template #default="scope">
        <div class="flex gap-4">
          <user-list-actions
            :user="scope.row"
            :action="UserActions.ACTIVATE_DEACTIVATE"
            :business-unit-code="businessUnitCode"
            :organization-code="organizationCode"
            @change="reloadWithContext"
          />
          <user-list-actions
            :user="scope.row"
            :action="UserActions.DELETE"
            :business-unit-code="businessUnitCode"
            :organization-code="organizationCode"
            @change="reloadWithContext"
          />
        </div>
      </template>
    </lxc-table-column>
  </lxc-table>
  <user-form
    v-model:show="showUserForm"
    :business-unit-code="businessUnitCode"
    :organization-code="organizationCode"
    :organization-type="organizationType"
    :user="currentSelectedUser"
    :use-external-idp="useExternalIdp"
    @close="onCloseCanvas"
  ></user-form>
</template>

<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
